import React, { useContext, useEffect, useMemo, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  ApiTypesEnum,
  CurrencyLockTypeEnum, DHLPackageDTO, DHLPickupCapabilitiesDTO, FedexPackageDTO,
  FeaturesEnum,
  FindCarrierResponseOutDTO,
  FindCarrierTypeEnum,
  PriceRequest, ValidationAttachmentInfoDTO,
  ValidationCargoTypeInDTO,
  ValidationDeliverySlotInDTO,
  ValidationOrderInDTO,
} from '@api/ringil-api.v2.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { AiCarrierAutoSelection, AiCarrierAutoSelectionDto } from '@components/ai/AiCarrierAutoSelection.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import { InputCurrencyLock } from '@components/ringil3/Input/InputCurrencyLock/InputCurrencyLock.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { InputText } from 'primereact/inputtext';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import CarrierDropdown from '@components/RingilComponents/CarrierDistributionSelector/CarrierDropdown.tsx';
import { DachserProductTerms } from '@components/RingilComponents/CarrierDistributionSelector/DachserProductTerms.tsx';
import {
  SchenkerProductTerms,
} from '@components/RingilComponents/CarrierDistributionSelector/SchenkerProductTerms.tsx';
import { ApiContext } from '@api/api.tsx';
import { useDebounce } from 'use-debounce';
import {
  CarrierValidationErrors,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierValidationErrors.tsx';
import SvgRepeat from '@icons/repeat.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { isNullOrUndefined, isSameArray, parseNumberOrUndefined } from '@utils/utils.tsx';
import dayjs from 'dayjs';
import { DHLProductTerms } from '@components/RingilComponents/CarrierDistributionSelector/DHLProductTerms.tsx';
import { FedexProductTerms } from '@components/RingilComponents/CarrierDistributionSelector/FedexProductTerms.tsx';
import InputDeadline, { DeadlineMode, DeadlineUnit } from '@components/RingilComponents/CarrierDistributionSelector/InputDeadline.tsx';
import { ContractedCarrierForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import ContractedCarriersSection from '@components/ringil3/Features/Contracted/ContractedCarriersSection.tsx';
import { contractedTotal, newPriceTable, resolveContractedPrices, tableLadenFactors } from '@utils/contractedPriceTable.ts';
import { muCodesOf } from '@app/pages/expedition/create/contractedUnits.ts';
import { formatPriceRequest } from '@utils/priceUtils.ts';

export interface Carrier {
  id: string;
  /** Optional: set when the carrier was loaded from a source that knows its name, for display only. */
  name?: string;
  carrierType?: FindCarrierTypeEnum;
  apiTypes?: ApiTypesEnum[];
  userIds?: string[];
  carrierIds?: string[];
  _dachserProductTypeCode?: string;
  _dachserDeliveryTermCode?: string;
  _schenkerProductTypeCode?: string;
  _schenkerAddonsTypeCodes?: string[];
  _dhlProductTypeCode?: string;
  _dhlCourierRequested?: boolean;
  _dhlPickupReadyTime?: string;
  _dhlPickupCloseTime?: string;
  _dhlPickupCapabilities?: DHLPickupCapabilitiesDTO;
  _dhlDifferentPayer?: boolean;
  _dhlPayerAccountNumber?: string;
  _dhlApplyToDutiesTaxes?: boolean;
  // raw FedEx serviceType code (service availability is lane/account-dependent)
  _fedexProductTypeCode?: string;
  _fedexCourierRequested?: boolean;
  _fedexPickupDate?: string;
  _fedexPickupReadyTime?: string;
  _fedexPickupCloseTime?: string;
  _fedexPayerType?: string;
  _fedexPayerAccountNumber?: string;
  _fedexPayerCountry?: string;
  _fedexDeclaredValue?: number;
  _fedexDeclaredValueCurrency?: string;
  _fedexSignatureOption?: string;
  _fedexPackagingType?: string;
  _fedexBookingConfirmationNumber?: string;
  // FedexAccount the shipment goes on (picked when the route allows several accounts)
  _fedexAccountId?: string;
  // UI only: the route allows several FedEx accounts, so one must be picked
  _fedexAccountRequired?: boolean;
}

/** Users picked for a direct assignment; empty means the whole carrier. */
export const getSelectedUserIds = (items?: Carrier[]): string[] => (items ?? []).flatMap(item => item.userIds ?? []);

const distinctCarrierIds = (items?: Carrier[]) => new Set((items ?? []).map(item => item.id)).size;

/**
 * Direct assignment targets one carrier. Several user rows of that carrier may be selected; picking a
 * row of another carrier, or the whole-carrier row, replaces the selection.
 */
const constrainToSingleCarrier = (prev: Carrier[], next: Carrier[]): Carrier[] => {
  const added = next.filter(n => !prev.some(p => p.id === n.id && isSameArray(p.userIds, n.userIds)));
  if (added.length === 0) return next;
  const anchor = added[added.length - 1];
  const wholeCarrier = !anchor.userIds?.length;
  return wholeCarrier ? [anchor] : next.filter(item => item.id === anchor.id && item.userIds?.length);
};

export interface CarrierDistributionSelectorData {
  distributionType: DistributionType;
  price?: PriceRequest;
  selectedItems: Carrier[];
  responseDeadlineMode?: DeadlineMode;
  responseDeadlineOffsetValue?: number;
  responseDeadlineOffsetUnit?: DeadlineUnit;
  responseDeadlineLocalTime?: dayjs.Dayjs;
  responseDeadlineTimezone?: string;
  notifyAssignedCarrier?: boolean;
  currencyLockType?: CurrencyLockTypeEnum;
  ladenFactor?: boolean;
  allowPartialDelivery?: boolean;
  ladenFactorValue?: number;
  /** CONTRACTED only: the contracted carriers with their price matrices, edited for this shipment only. */
  contractedCarriers?: ContractedCarrierForm[];
  /** CONTRACTED only: price table structure + plan of levels, edited for this shipment only. */
  contractedPriceTable?: PriceTableForm;
}

interface LocationValidation {
  id?: string;
  companyLocationId?: string;
  city: string;
  country: string;
  postalCode: string;
  street?: string;
  orders?: ValidationOrderInDTO[];
  deliverySlot?: ValidationDeliverySlotInDTO;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
}

interface ExpeditionValidation {
  locations: LocationValidation[];
  cargoType?: ValidationCargoTypeInDTO;
  incotermsValue?: string;
  incotermsDescription?: string;
  polandSentNumber?: string;
  polandSentCarrierUpdateKey?: string;
  huEkaerNumber?: string;
  roUitNumber?: string;
  utnCode?: string;
  attachments: ValidationAttachmentInfoDTO[]
}

interface ComponentProps {
  allowedDistributionTypes?: DistributionType[];
  distribution: CarrierDistributionSelectorData;
  originalDistribution?: CarrierDistributionSelectorData;
  onChange: (distribution: CarrierDistributionSelectorData) => void;
  validationErrors?: { path: string; message: string }[];
  aiCarrierAutoSelectionDto?: AiCarrierAutoSelectionDto;
  handlingUnitCount?: number;
  ladenFactorEligible?: boolean;
  defaultLadenFactor?: number;
  onlyCarriers?: boolean;
  expeditionValidations?: ExpeditionValidation[];
  pickupTimezoneId?: string;
  dataTestIdPrefix?: string;
  /** Leave API carriers (Dachser / Schenker / DHL) out of the candidates — bulk tendering cannot validate them per route. */
  excludeApiCarriers?: boolean;
  /** Hide the AI auto-selection row (it needs a single expedition to recommend for). */
  hideAiAutoSelection?: boolean;
  /** Bulk tendering: no single expedition to derive divisors from — free positive-integer input, no trucks hint. */
  ladenFactorFreeInput?: boolean;
  /**
   * CONTRACTED: the shipment's handling units per manipulation unit (cargo template code). Drives the
   * per-carrier price preview, the missing-price flags and the single-MU rule for laden factor.
   */
  contractedUnitsByMu?: Record<string, number>;
  /** CONTRACTED: shown in the contracted-prices sidebar, e.g. that edits apply to this shipment only. */
  contractedInfoText?: string;
  /** CONTRACTED: name of the Linka the prices came from, shown in the sidebars. */
  contractedSubjectName?: string;
}

export enum DistributionType {
  ASSIGN = 'ASSIGN',
  DO_NOT_ASSIGN = 'DO_NOT_ASSIGN',
  FIXED = 'FIXED',
  SPOT = 'SPOT',
  /** "Smluvní dopravce": carriers accept at the contracted price, gated by the CONTRACTED_CARRIERS feature. */
  CONTRACTED = 'CONTRACTED',
}

/**
 * RD-2978: a shipment from a Linka is distributed only to its contracted carriers, any other never to them.
 * [types] = what the caller offers an ordinary shipment; DO_NOT_ASSIGN (no distribution at all) stays for both.
 */
export const distributionTypesForShipment = (fromLine: boolean, types: DistributionType[]): DistributionType[] =>
  fromLine
    ? [...types.filter(type => type === DistributionType.DO_NOT_ASSIGN), DistributionType.CONTRACTED]
    : types.filter(type => type !== DistributionType.CONTRACTED);

/** Every type except CONTRACTED, which a caller has to opt into explicitly. */
const DEFAULT_ALLOWED_DISTRIBUTION_TYPES = Object.values(DistributionType).filter(type => type !== DistributionType.CONTRACTED);

/** Shown (and validated) when a laden factor is set on a CONTRACTED shipment with more than one manipulation unit. */
export const contractedMixedMuMessage = (tr: (key: string, defaultText?: string) => string) =>
  tr('CarrierDistributionSelector.contractedMixedMu', 'Při zadané kapacitě musí přeprava obsahovat jen jeden typ manipulační jednotky');

export const resolveResponseDeadline = (distribution: CarrierDistributionSelectorData, defaultTimezone: string): { localTime: dayjs.Dayjs; timezone: string } => {
  if (distribution.responseDeadlineMode === 'absolute' && distribution.responseDeadlineLocalTime) {
    return {
      localTime: distribution.responseDeadlineLocalTime,
      timezone: distribution.responseDeadlineTimezone || defaultTimezone,
    };
  }
  const offsetValue = distribution.responseDeadlineOffsetValue ?? 8;
  const offsetUnit = distribution.responseDeadlineOffsetUnit ?? 'hours';
  const offsetHours = offsetUnit === 'days' ? offsetValue * 24 : offsetValue;
  return {
    localTime: dayjs().add(offsetHours, 'hours'),
    timezone: distribution.responseDeadlineTimezone || defaultTimezone,
  };
};

const CarrierDistributionSelector: React.FC<ComponentProps> = ({
                                                                 allowedDistributionTypes = DEFAULT_ALLOWED_DISTRIBUTION_TYPES,
                                                                 distribution,
                                                                 originalDistribution,
                                                                 onChange,
                                                                 validationErrors,
                                                                 aiCarrierAutoSelectionDto,
                                                                 handlingUnitCount,
                                                                 ladenFactorEligible,
                                                                 defaultLadenFactor,
                                                                 onlyCarriers = false,
                                                                 expeditionValidations,
                                                                 pickupTimezoneId,
                                                                 dataTestIdPrefix,
                                                                 excludeApiCarriers = false,
                                                                 hideAiAutoSelection = false,
                                                                 ladenFactorFreeInput = false,
                                                                 contractedUnitsByMu,
                                                                 contractedInfoText,
                                                                 contractedSubjectName,
                                                               }) => {

  const { tr } = useTranslationLgs();
  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
  const { cuValidateCarriersForExpeditionsV3 } = useContext(ApiContext);
  const [debouncedExpeditionValidations] = useDebounce(expeditionValidations, 500);

  const [numberOfTrucks, setNumberOfTrucks] = useState<string>('N/A');
  const [carriers, setCarriers] = useState<FindCarrierResponseOutDTO[]>([]);
  const [filteredCarriers, setFilteredCarriers] = useState<FindCarrierResponseOutDTO[]>([]);

  const currencyLockEnabled = hasFeature([FeaturesEnum.CURRENCY_LOCK]);
  const contractedEnabled = hasFeature([FeaturesEnum.CONTRACTED_CARRIERS]);
  const isContracted = distribution?.distributionType === DistributionType.CONTRACTED;
  // a Linka can switch the laden factor on for orders that are not eligible (e.g. mixed MUs); the switch has to stay
  // visible then, or the user could not turn off what blocks the save
  const ladenFactorEnabled = hasFeature([FeaturesEnum.LADEN_FACTOR]) && (ladenFactorEligible || (isContracted && !!distribution?.ladenFactor));

  const priceError = validationErrors?.find(e => e.path === 'distribution.price.price')?.message;
  const selectedCarriersError = validationErrors?.find(e => e.path === 'distribution.selectedItems')?.message;
  const deadlineError = validationErrors?.find(e => e.path === 'distribution.responseDeadlineOffsetValue' || e.path === 'distribution.responseDeadlineLocalTime')?.message;


  // Whether the expedition default laden factor divides the handling unit count without a reminder (i.e. a "computed" option).
  const defaultLadenFactorWithoutReminder = useMemo(
    () => !!handlingUnitCount && !!defaultLadenFactor && handlingUnitCount % defaultLadenFactor === 0,
    [handlingUnitCount, defaultLadenFactor],
  );

  const contractedTable = isContracted ? (distribution?.contractedPriceTable ?? null) : null;

  // CONTRACTED without a table (blank form, switched type): create it once and keep it in the distribution, so its
  // level keys stay stable for the matrices typed against them
  useEffect(() => {
    if (!isContracted || distribution?.contractedPriceTable) return;
    const mu = muCodesOf(contractedUnitsByMu ?? {})[0] ?? null;
    onChange({ ...distribution, contractedPriceTable: newPriceTable(loggedUser?.defaultCurrencyIsoCode ?? 'EUR', tr('PriceTable.levelPrefix', 'Úroveň'), mu) });
  }, [isContracted, distribution?.contractedPriceTable]);

  const ladenFactorOptions = useMemo(() => {
    // CONTRACTED: the laden factors the price table has rows for ("1 vozidlo veze 7 FBU" with a single MU)
    const contractedLfs = tableLadenFactors(contractedTable);
    if (contractedLfs.length) {
      const mus = Array.from(new Set(contractedTable.rows.map(r => r.cargoTemplateName || r.cargoTemplateCode)));
      return contractedLfs.map(value => ({ value, label: mus.length === 1 ? `${value} ${mus[0]}` : `${value}` }));
    }
    if (!handlingUnitCount) return [];
    const values = Array.from({ length: handlingUnitCount }, (_, i) => i + 1)
      .filter(num => handlingUnitCount % num === 0);
    // Include the expedition default laden factor even if it is not an even divisor of the handling unit count,
    // but never when it is bigger than all computed values.
    if (defaultLadenFactor && !values.includes(defaultLadenFactor) && defaultLadenFactor <= Math.max(...values)) {
      values.push(defaultLadenFactor);
    }
    return values
      .sort((a, b) => a - b)
      .map(value => ({ value, label: value.toString() }));
  }, [handlingUnitCount, defaultLadenFactor, contractedTable?.rows]);

  const calculatedTrucks = useMemo(() => {
    if (distribution?.ladenFactorValue && handlingUnitCount) {
      return (handlingUnitCount / distribution.ladenFactorValue).toString();
    }
    return 'N/A';
  }, [distribution?.ladenFactorValue, handlingUnitCount]);

  useEffect(() => {
      cuValidateCarriersForExpeditionsV3(
        {
          q: '',
          expeditionValidations,
        },
        data => {
          setCarriers(data);
          // A previously selected carrier may have just become unavailable (e.g. the
          // orders changed and the FedEx/DHL validation now fails) - drop it from the
          // selection, otherwise its product-terms block would linger with stale data.
          const unavailableIds = new Set(
            data.filter(carrier => carrier.unavailableMessages && carrier.unavailableMessages.length > 0).map(carrier => carrier.id),
          );
          const selected = distribution?.selectedItems || [];
          if (selected.some(item => unavailableIds.has(item.id))) {
            onChange({
              ...distribution,
              selectedItems: selected.filter(item => !unavailableIds.has(item.id)),
            });
          }
        },
      );
    }, [debouncedExpeditionValidations],
  );

  useEffect(() => {
    setNumberOfTrucks(calculatedTrucks);
  }, [calculatedTrucks]);

  const model = [
    {
      code: DistributionType.DO_NOT_ASSIGN,
      label: tr('CarrierDistributionSelector.distributionTypeDoNotAssign', 'Do not assign'),
      dataTestId: 'doNotAssign',
    },
    { code: DistributionType.SPOT, label: tr('CarrierDistributionSelector.distributionTypeSpot', 'Spot'), dataTestId: 'spotPrice' },
    { code: DistributionType.FIXED, label: tr('CarrierDistributionSelector.distributionTypeFixed', 'Fixed'), dataTestId: 'fixedPrice' },
    { code: DistributionType.ASSIGN, label: tr('CarrierDistributionSelector.distributionTypeAssign', 'Assign'), dataTestId: 'assignCarrier' },
    ...(contractedEnabled
      ? [{ code: DistributionType.CONTRACTED, label: tr('CarrierDistributionSelector.distributionTypeContracted', 'Smluvní dopravce'), dataTestId: 'contracted' }]
      : []),
  ];

  const filteredModel = model.filter(item => allowedDistributionTypes.includes(item.code));

  useEffect(() => {
    const base = excludeApiCarriers ? carriers.filter(carrier => carrier.findCarrierType !== FindCarrierTypeEnum.API) : carriers;
    if (distribution?.distributionType === DistributionType.ASSIGN) {
      // Direct assignment targets one carrier, optionally narrowed to one of its users (notification only).
      setFilteredCarriers(
        base.filter(
          carrier => carrier.findCarrierType === FindCarrierTypeEnum.CARRIER || carrier.findCarrierType === FindCarrierTypeEnum.CARRIER_USER,
        ),
      );
    } else {
      setFilteredCarriers(base);
    }
  }, [distribution?.distributionType, carriers, excludeApiCarriers]);

  if (!distribution) {
    return null;
  }

  const getDistributionFromDistribution = (dist: CarrierDistributionSelectorData, distributionType: DistributionType) => {
    return {
      ...dist,
      distributionType: distributionType,
      selectedItems:
        distributionType === DistributionType.ASSIGN && (
          distinctCarrierIds(dist.selectedItems) > 1 ||
          (dist.selectedItems?.some(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER)))
        )
          ? []
          : dist.selectedItems,
    };
  };

  const getDistributionOnTypeSwitch = (distributionType: DistributionType) => {
    if (distributionType === DistributionType.DO_NOT_ASSIGN) {
      return {
        distributionType: distributionType,
        selectedItems: [] as Carrier[],
      };
    } else if (distribution.distributionType === DistributionType.DO_NOT_ASSIGN && originalDistribution) {
      return getDistributionFromDistribution(originalDistribution, distributionType);
    }

    return getDistributionFromDistribution(distribution, distributionType);
  };

  const contractedMuCodes = muCodesOf(contractedUnitsByMu ?? {});
  const contractedLadenFactor = distribution.ladenFactor ? distribution.ladenFactorValue : null;
  const contractedMixedMu = isContracted && distribution.ladenFactor && contractedMuCodes.length > 1;
  // the level of the plan valid on the deadline day prices the shipment (same as the backend)
  const contractedDeadline = isContracted ? resolveResponseDeadline(distribution, loggedUser?.defaultTimezoneId ?? 'Europe/Prague') : null;
  const contractedDeadlineDate = contractedDeadline ? contractedDeadline.localTime.tz(contractedDeadline.timezone).format('YYYY-MM-DD') : undefined;
  const contractedResolved = isContracted
    ? resolveContractedPrices(contractedTable, distribution.contractedCarriers ?? [], contractedDeadlineDate, contractedLadenFactor, contractedMuCodes)
    : null;
  const contractedCarriersError = validationErrors
    ?.filter(e => e.path === 'distribution.contractedCarriers' || e.path?.startsWith('distribution.contractedCarriers[') || e.path === 'distribution.contractedPriceTable')
    .map(e => e.message)
    .join(' ');

  return (

    // CONTRACTED shows the level timeline and the carriers' prices, which need the whole card
    <div className={isContracted ? 'flex flex-col w-full gap-4' : 'flex flex-col w-[500px] gap-4'}>
      {!onlyCarriers && (
        <Field label={tr('CarrierDistributionSelector.distributionType', 'DistributionType')}>
          <InputSelect
            model={filteredModel}
            selectedItemCode={distribution?.distributionType}
            onSelect={v => {
              onChange(getDistributionOnTypeSwitch(v as DistributionType));
            }}
            dataTestIdPrefix={dataTestIdPrefix}
          />
        </Field>
      )}
      {distribution?.distributionType !== DistributionType.DO_NOT_ASSIGN && (
        <>
          {originalDistribution && (
            <RingilButton
              label={tr('CarrierDistributionSelector.loadFromOriginalShipment', 'Load from original shipment')}
              mode={'table'}
              icon={<SvgRepeat />}
              onClick={() => onChange(getDistributionFromDistribution(originalDistribution, distribution.distributionType))}
              className="w-fit"
            />
          )}
          {
            distribution?.distributionType !== 'ASSIGN' && ladenFactorEnabled && (
              <>
                <InputCheckbox
                  value={distribution.ladenFactor}
                  onChange={(_: string, checked: boolean) => {
                    onChange({ ...distribution, ladenFactor: checked });
                  }}
                  label={tr(`CarrierDistributionSelector.ladenFactor`, `Enable laden factor`)}
                />

                {distribution.ladenFactor && (
                  <>
                    <Field
                      label={tr('CarrierDistributionSelector.ladenFactorLabel', 'Enter laden factor')}
                      required
                      helpText={
                        ladenFactorFreeInput
                          ? tr('CarrierDistributionSelector.ladenFactorHelpBulk', 'Celé číslo; počet manipulačních jednotek každé přepravy musí být jeho násobkem.')
                          : tr('CarrierDistributionSelector.ladenFactorHelp', 'Laden factor value help')
                      }
                      errorMessage={validationErrors?.find(e => e.path === 'distribution.ladenFactorValue')?.message}
                    >
                      {ladenFactorFreeInput ? (
                        <InputText
                          value={distribution.ladenFactorValue?.toString() ?? ''}
                          keyfilter={'pint'}
                          onChange={e => onChange({ ...distribution, ladenFactorValue: parseNumberOrUndefined(e.target.value) })}
                          className={'w-full'}
                          data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-ladenFactorValue` : undefined}
                        />
                      ) : (
                        <Dropdown
                          options={ladenFactorOptions}
                          optionValue="value"
                          optionLabel="label"
                          value={distribution.ladenFactorValue}
                          onChange={(e) => onChange({ ...distribution, ladenFactorValue: e.value })}
                          style={{ width: '100%', padding: '0.5rem', color: 'var(--ringil3-gr4)' }}
                          placeholder={tr('CarrierDistributionSelector.selectLadenFactor', 'Select laden factor')}
                        />
                      )}
                    </Field>
                    {!ladenFactorFreeInput && !isNullOrUndefined(distribution.ladenFactorValue) && distribution.ladenFactorValue > 0 && (
                      <COInlineMessage
                        variant={distribution.ladenFactorValue === defaultLadenFactor && !defaultLadenFactorWithoutReminder ? 'alert' : 'info'}>
                        {tr('CarrierDistributionSelector.ladenFactorNumberOfTrucks', 'Based on provided laden factor, number of trucks needed is: {{numberOfTrucks}}', { numberOfTrucks })}
                      </COInlineMessage>
                    )}
                    {contractedMixedMu && <COInlineMessage variant={'alert'}>{contractedMixedMuMessage(tr)}</COInlineMessage>}
                  </>
                )}
              </>
            )}
          {
            distribution?.distributionType !== 'ASSIGN' && ladenFactorEnabled && ladenFactorEligible && distribution.ladenFactor && (
              <InputCheckbox
                value={distribution.allowPartialDelivery}
                onChange={(key: string, checked: boolean) => {
                  onChange({ ...distribution, allowPartialDelivery: checked });
                }}
                label={tr(`CarrierDistributionSelector.allowPartialDeliveryLabel`, `Allow partial delivery`)}
                note={tr(`CarrierDistributionSelector.allowPartialDeliveryNote`, `If enabled, carriers can offer to deliver only part of the shipment.`)}
              />
            )}

          {isContracted && contractedTable && (
            <div className={'flex flex-col gap-2'}>
              <ContractedCarriersSection
                mode={'expedition'}
                table={contractedTable}
                carriers={distribution.contractedCarriers ?? []}
                onChange={({ table, carriers }) => onChange({ ...distribution, contractedPriceTable: table, contractedCarriers: carriers })}
                subjectName={contractedSubjectName}
                deadlineDate={contractedDeadlineDate}
                shipmentLadenFactor={contractedLadenFactor}
                shipmentMuCodes={contractedMuCodes}
                infoText={contractedInfoText ?? tr('CarrierDistributionSelector.contractedInfoText', 'Změny platí jen pro tuto přepravu.')}
                errorMessage={contractedCarriersError || undefined}
              />
              {contractedMuCodes.length > 0 &&
                (distribution.contractedCarriers ?? []).map(carrier => {
                  const total = contractedTotal(contractedResolved, carrier.carrierId, contractedUnitsByMu);
                  if (total === null) return null;
                  return (
                    <div key={carrier.carrierId} className={'text-sm text-content-secondary'} data-testid={`contracted-total-${carrier.carrierId}`}>
                      {tr('CarrierDistributionSelector.contractedTotal', '{{carrier}} – Cena celkem: {{price}}', {
                        carrier: carrier.carrierName,
                        price: formatPriceRequest({ price: total, currency: contractedTable.currency }),
                      })}
                    </div>
                  );
                })}
            </div>
          )}

          {!onlyCarriers && !isContracted && (
            <div
              className="flex flex-row items-center justify-between gap-4"
            >
              <Field
                label={tr('CarrierDistributionSelector.price', 'Price')}
                required
                skipCurrencyCssMode={true}
                errorMessage={priceError}
              >
                <InputPriceWithCurrency
                  value={distribution.price?.price}
                  currency={distribution.price?.currency}
                  changePrice={v => onChange({ ...distribution, price: { ...distribution.price, price: v } })}
                  changeCurrency={v => onChange({ ...distribution, price: { ...distribution.price, currency: v } })}
                  companyId={loggedUser.companyId}
                  dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-price` : undefined}
                  dataTestIdCurrency={dataTestIdPrefix ? `${dataTestIdPrefix}-priceCurrency` : undefined}
                />
              </Field>
              {currencyLockEnabled && distribution?.distributionType === 'SPOT' && (
                <Field
                  label={tr('CarrierDistributionSelector.allowedCurrenciesLabel', 'Allowed currencies')}
                  required
                  helpText={tr('CarrierDistributionSelector.allowedCurrenciesHelp', 'Allowed currencies help')}
                >
                  <InputCurrencyLock
                    value={distribution.currencyLockType}
                    onChange={v => onChange({ ...distribution, currencyLockType: v })}
                  />
                </Field>
              )}
            </div>
          )}

          {!isContracted && (
          <>
          <div>
            <Field
              label={tr('CarrierDistributionSelector.carrier', 'Carrier')}
              required
              errorMessage={selectedCarriersError}
            >
              <CarrierDropdown
                values={distribution.selectedItems}
                carriers={filteredCarriers.filter(carrier => !carrier.unavailableMessages || carrier.unavailableMessages.length === 0)}
                onSelect={values => {
                  onChange({
                    ...distribution,
                    selectedItems:
                      distribution.distributionType === DistributionType.ASSIGN
                        ? constrainToSingleCarrier(distribution.selectedItems || [], values)
                        : values,
                  });
                }}
                multiSelect={true}
                selectAllEnabled={distribution?.distributionType !== 'ASSIGN'}
                dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-carrierList` : undefined}
              />
            </Field>
            {!hideAiAutoSelection && (
            <AiCarrierAutoSelection
              label={tr('CarrierDistributionSelector.aiAutoSelectionLabel', 'AI Auto-selection')}
              aiCarrierAutoSelectionDto={aiCarrierAutoSelectionDto}
              onSelect={values => {
                const merged = Array.from(new Set([...(distribution.selectedItems || []), ...values]));
                onChange({
                  ...distribution,
                  selectedItems:
                    distribution.distributionType === DistributionType.ASSIGN
                      ? constrainToSingleCarrier(distribution.selectedItems || [], merged)
                      : merged,
                });
              }}
              allowedCarrierTypes={distribution?.distributionType !== 'ASSIGN' ? [] : [FindCarrierTypeEnum.CARRIER]}
              multiSelect={distribution?.distributionType !== 'ASSIGN'}
              dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-aiCarrier-theBest` : undefined}
              selectedCarriers={distribution.selectedItems}
            />
            )}
          </div>

          <CarrierValidationErrors
            carriers={filteredCarriers.filter(carrier => carrier.unavailableMessages && carrier.unavailableMessages.length > 0)}
          />

          <DachserProductTerms
            values={distribution.selectedItems}
            onSelect={values => {
              const filtered = (distribution.selectedItems || []).filter(
                item => !(item.apiTypes && item.apiTypes.includes(ApiTypesEnum.DACHSER))
              );

              const unique = [
                ...filtered,
                ...(values || []),
              ];

              onChange({
                ...distribution,
                selectedItems: unique,
              });
            }}
          />

          <SchenkerProductTerms
            values={distribution.selectedItems}
            onSelect={values => {
              const filtered = (distribution.selectedItems || []).filter(
                item => !(item.apiTypes && item.apiTypes.includes(ApiTypesEnum.SCHENKER))
              );

              const unique = [
                ...filtered,
                ...(values || []),
              ];

              onChange({
                ...distribution,
                selectedItems: unique,
              });
            }}
          />

          {expeditionValidations && expeditionValidations.length > 0 && (
            <DHLProductTerms
              pickupTimezoneId={pickupTimezoneId}
              productParameters={{
                countryFrom: expeditionValidations[0]?.locations[0]?.country,
                countryTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.country,
                postalCodeFrom: expeditionValidations[0]?.locations[0]?.postalCode,
                postalCodeTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.postalCode,
                cityFrom: expeditionValidations[0]?.locations[0]?.city,
                cityTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.city,
                streetFrom: expeditionValidations[0]?.locations[0]?.street,
                streetTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.street,
                fromCompanyLocationId: expeditionValidations[0]?.locations[0]?.companyLocationId,
                toCompanyLocationId: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.companyLocationId,
                fromDeliverySlot: expeditionValidations[0]?.locations[0]?.deliverySlot,
                allPackages: (expeditionValidations?.[0]?.locations ?? [])
                  .flatMap(location => location.orders ?? [])
                  .flatMap(order => order?.items ?? []) as DHLPackageDTO[],
              }}
              values={distribution.selectedItems}
              onSelect={values => {
                const filtered = (distribution.selectedItems || []).filter(
                  item => !(item.apiTypes && item.apiTypes.includes(ApiTypesEnum.DHL)),
                );

                const unique = [
                  ...filtered,
                  ...(values || []),
                ];

                onChange({
                  ...distribution,
                  selectedItems: unique,
                });
              }}
            />
          )}

          {expeditionValidations && expeditionValidations.length > 0 && (
            <FedexProductTerms
              pickupTimezoneId={pickupTimezoneId}
              productParameters={{
                countryFrom: expeditionValidations[0]?.locations[0]?.country,
                countryTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.country,
                postalCodeFrom: expeditionValidations[0]?.locations[0]?.postalCode,
                postalCodeTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.postalCode,
                cityFrom: expeditionValidations[0]?.locations[0]?.city,
                cityTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.city,
                streetFrom: expeditionValidations[0]?.locations[0]?.street,
                streetTo: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.street,
                fromCompanyLocationId: expeditionValidations[0]?.locations[0]?.companyLocationId,
                toCompanyLocationId: expeditionValidations[0]?.locations[expeditionValidations[0]?.locations?.length - 1]?.companyLocationId,
                fromDeliverySlot: expeditionValidations[0]?.locations[0]?.deliverySlot,
                allPackages: (expeditionValidations?.[0]?.locations ?? [])
                  .flatMap(location => location.orders ?? [])
                  .flatMap(order => order?.items ?? []) as FedexPackageDTO[],
              }}
              values={distribution.selectedItems}
              onSelect={values => {
                const filtered = (distribution.selectedItems || []).filter(
                  item => !(item.apiTypes && item.apiTypes.includes(ApiTypesEnum.FEDEX)),
                );

                const unique = [
                  ...filtered,
                  ...(values || []),
                ];

                onChange({
                  ...distribution,
                  selectedItems: unique,
                });
              }}
            />
          )}
          </>
          )}

          {!onlyCarriers && distribution?.distributionType !== 'ASSIGN' && (
            <Field label={tr('CarrierDistributionSelector.offerDeadline', 'Offer deadline')} required errorMessage={deadlineError}>
              <InputDeadline
                mode={distribution.responseDeadlineMode || 'relative'}
                offsetValue={distribution.responseDeadlineOffsetValue}
                offsetUnit={distribution.responseDeadlineOffsetUnit || 'hours'}
                absoluteLocalTime={distribution.responseDeadlineLocalTime}
                absoluteTimezone={distribution.responseDeadlineTimezone}
                value={(distribution.responseDeadlineMode || 'relative') === 'relative' ? distribution.responseDeadlineOffsetValue : distribution.responseDeadlineLocalTime}
                onChangeRelative={(value, unit) => {
                  onChange({
                    ...distribution,
                    responseDeadlineMode: 'relative',
                    responseDeadlineOffsetValue: value,
                    responseDeadlineOffsetUnit: unit,
                  });
                }}
                onChangeAbsolute={(localDate, timezoneId) => {
                  onChange({
                    ...distribution,
                    responseDeadlineMode: 'absolute',
                    responseDeadlineLocalTime: localDate as dayjs.Dayjs,
                    responseDeadlineTimezone: timezoneId,
                  });
                }}
                onModeChange={(mode) => {
                  onChange({
                    ...distribution,
                    responseDeadlineMode: mode,
                  });
                }}
                dataTestIdCalendar={dataTestIdPrefix ? `${dataTestIdPrefix}-demandValidTill` : undefined}
                dataTestIdTimezone={dataTestIdPrefix ? `${dataTestIdPrefix}-demandValidTillTimeZone` : undefined}
              />
            </Field>
          )}
          {!onlyCarriers && distribution?.distributionType === 'ASSIGN' && (
            <div className={'flex flex-row  items-start mt-2'}>
              <InputCheckbox
                label={tr('CarrierDistributionSelector.informCarrierAboutAssignmentLabel', 'Inform carrier about assignment')}
                note={tr('CarrierDistributionSelector.informCarrierAboutAssignmentNote', 'We will send e-mail to carrier')}
                dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-notifyAssignedCarrier` : undefined}
                value={distribution.notifyAssignedCarrier}
                onChange={(v, value) => onChange({ ...distribution, notifyAssignedCarrier: value })}
              />
            </div>
          )}
        </>
      )}

      </div>
  );
};

export default CarrierDistributionSelector;
